import { ApiError } from "@/api/client";

/**
 * Maps backend error codes / messages to user-friendly Bahasa
 * Indonesia strings for surfacing in toasts and alerts. The
 * default carries the original ``message`` field from the API,
 * which is already Indonesian for most validation errors.
 */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  API_KEY_AUTH_FAILED: "Autentikasi API key gagal",
  RATE_LIMIT_EXCEEDED: "Rate limit terlampaui, coba lagi sebentar",
  MODEL_NOT_ALLOWED: "Model tidak diizinkan untuk tenant ini",
  INVALID_REQUEST: "Request tidak valid",
  NOT_FOUND: "Resource tidak ditemukan",
  FORBIDDEN: "Tidak ada izin untuk action ini",
  API_KEY_NOT_FOUND: "API key tidak ditemukan",
  API_KEY_INVALID_STATE: "API key dalam state yang tidak valid",
  API_KEY_PERMISSION_DENIED: "Tidak ada izin untuk action ini",
  WORKSPACE_NOT_FOUND: "Workspace tidak ditemukan",
  // Policy engine
  POLICY_VALIDATION_FAILED:
    "Format kebijakan tidak valid. Periksa kembali bidang yang dibutuhkan.",
  POLICY_DSL_SYNTAX_ERROR: "Sintaks DSL kebijakan tidak valid.",
  POLICY_FRAMEWORK_INVALID: "Framework compliance tidak dikenali.",
  POLICY_NOT_FOUND: "Kebijakan tidak ditemukan.",
  POLICY_VERSION_NOT_FOUND: "Versi kebijakan tidak ditemukan.",
  // Sesi FE-PROVIDER-SETTINGS — BYOK credential lifecycle.
  PROVIDER_KEY_INVALID:
    "Kunci API tidak valid. Periksa kembali kunci dari penyedia AI Anda.",
  PROVIDER_CREDENTIAL_NOT_FOUND:
    "Kredensial penyedia AI tidak ditemukan. Mungkin sudah dihapus atau dicabut.",
  PROVIDER_CREDENTIAL_NAME_CONFLICT:
    "Nama kredensial sudah digunakan untuk penyedia ini. Pilih nama yang berbeda.",
  PROVIDER_CONFIG_ERROR:
    "Konfigurasi penyedia AI tidak valid. Periksa base URL atau pengaturan terkait.",
  PROVIDER_UNSUPPORTED:
    "Penyedia AI tersebut belum didukung untuk validasi otomatis.",
  // Sesi POLISH-NAV-API — invitation quota (5 aktif per tenant).
  PILOT_INVITATION_LIMIT_REACHED:
    "Batas undangan pilot tercapai (5 aktif). Cabut undangan yang tidak digunakan untuk menambah anggota baru.",
  // Sesi WORKSPACE-MGMT — workspace + membership errors.
  // ``WORKSPACE_NOT_FOUND`` declared above; not duplicated here.
  WORKSPACE_SLUG_CONFLICT:
    "Slug workspace sudah digunakan. Pilih slug yang berbeda.",
  WORKSPACE_LIMIT_EXCEEDED:
    "Batas workspace tercapai (100 per tenant). Arsipkan workspace yang tidak digunakan.",
  CANNOT_ARCHIVE_DEFAULT_WORKSPACE:
    "Workspace default tidak dapat diarsipkan. Set workspace lain sebagai default terlebih dahulu.",
  MEMBERSHIP_NOT_FOUND: "Membership tidak ditemukan.",
  USER_ALREADY_MEMBER:
    "Pengguna sudah menjadi anggota workspace ini.",
};

/**
 * Bahasa Indonesia translations for the pydantic-v2 ``msg`` strings
 * that surface most often on the policy admin surface. The map is
 * keyed by exact match so the fallback (raw English) is visible
 * during dev — easier to spot new messages worth translating.
 */
const PYDANTIC_MESSAGE_ID: Record<string, string> = {
  "Field required": "wajib diisi",
  "field required": "wajib diisi",
  "Extra inputs are not permitted": "field tidak dikenali",
  "Input should be a valid string": "harus berupa string",
  "Input should be a valid integer": "harus berupa angka",
  "Input should be a valid boolean": "harus boolean (true/false)",
  "Input should be a valid UUID": "format UUID tidak valid",
  "value is not a valid uuid": "format UUID tidak valid",
  "String should have at least 1 character": "tidak boleh kosong",
};

export function parseApiError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code && ERROR_CODE_MESSAGES[error.code]) {
      return ERROR_CODE_MESSAGES[error.code];
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return "Terjadi kesalahan tak terduga.";
}

/**
 * Extract structured pydantic 422 details from an ``ApiError`` and
 * format each field-level issue into a Bahasa Indonesia one-liner.
 *
 * Pydantic v2 ships the error list under different shapes depending
 * on how the backend wraps it. We handle two:
 *
 * 1. ``payload.details`` is an array of pydantic dicts (the
 *    Monago typed-error envelope wraps validation errors this way).
 * 2. ``payload.detail`` is a FastAPI-default 422 envelope. Same
 *    pydantic dict shape, different key.
 *
 * Returns an empty array when the error isn't a structured 422 — the
 * caller falls back to ``parseApiError`` for the title-level message.
 */
export function parseValidationDetail(error: unknown): string[] {
  if (!(error instanceof ApiError)) return [];
  if (typeof error.payload !== "object" || error.payload === null) return [];

  const payload = error.payload as Record<string, unknown>;
  const candidate =
    (Array.isArray(payload.details) && payload.details) ||
    (Array.isArray(payload.detail) && payload.detail) ||
    null;
  if (!candidate) return [];

  const lines: string[] = [];
  for (const item of candidate as Array<Record<string, unknown>>) {
    if (typeof item !== "object" || item === null) continue;
    const loc = Array.isArray(item.loc) ? item.loc : [];
    // Drop the leading ``body``/``query`` segment that pydantic
    // injects via FastAPI — UI surface is per-field.
    const fieldPath = loc
      .slice(loc[0] === "body" || loc[0] === "query" ? 1 : 0)
      .filter((part): part is string | number =>
        typeof part === "string" || typeof part === "number"
      )
      .join(".");
    const rawMsg = typeof item.msg === "string" ? item.msg : "";
    const friendly = PYDANTIC_MESSAGE_ID[rawMsg] ?? rawMsg;
    if (!fieldPath && !friendly) continue;
    lines.push(
      fieldPath
        ? `Bidang “${fieldPath}”: ${friendly}`
        : friendly
    );
  }
  return lines;
}

/**
 * Some backend paths re-wrap an error payload inside FastAPI's
 * ``detail`` field, which renders as a Python-dict literal when
 * the framework serialises it. The Bahasa Indonesia string from
 * ERROR_CODE_MESSAGES is the right thing to show as the headline
 * — this helper extracts the *inner* upstream reason so the FE
 * can surface it as a "Detail:" line. Two envelope shapes
 * covered: Python dict repr (single-quoted), and an embedded
 * JSON ``{"message": "…"}`` blob from the upstream provider.
 *
 * Falls back to the raw string when nothing matches — callers
 * decide whether the raw form is presentable or should be hidden.
 */
export function unwrapNestedErrorMessage(raw: string): string {
  if (!raw) return raw;
  const pythonDict = raw.match(
    /\{'error'\s*:\s*'[^']+'\s*,\s*'message'\s*:\s*'([^']+)'\s*\}/
  );
  if (pythonDict) return pythonDict[1];
  const jsonMessage = raw.match(/"message"\s*:\s*"([^"]+)"/);
  if (jsonMessage) return jsonMessage[1];
  return raw;
}
